import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Bug, Server, Eye, GitBranch, Cloud, Network, Code2,
  ClipboardCheck, ScanLine, CalendarClock, FileText, KeyRound, Lock,
  Briefcase, UserPlus, Building2, Users, BellRing, Settings2, ShieldCheck,
  Menu, X, FileSearch,
} from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useClient } from '../../context/ClientContext';
import { useImpersonation } from '../../context/ImpersonationContext';
import { useUser } from '../../context/UserContext';
import { useBranding } from '../../context/BrandingContext';
import { useClientProfile, useActiveScan, useMetricsRollup, useSddFindings, useFindings } from '../../hooks/useClientData';
import { useAllFindings } from '../../context/FindingsContext';
import { normalizeFinding } from '../../utils/severity';
import { isADFinding, isCloudFinding, isDastFinding, isVulnerabilityListFinding } from '../../utils/findingClassification';
import { summarizeVisibleSensitiveData } from '../../utils/sensitiveDataCounts';
import { isOsintCoverageDiagnostic } from '../../utils/osintCoverage';
import TierBadge from '../badges/TierBadge';
import BrandLogo from '../ui/BrandLogo';

const CLIENT_NAV = [
  { l: 'Dashboard', p: '/client/dashboard', i: LayoutDashboard, t: 'The client-facing security summary and next actions.' },
  { section: 'Identify' },
  { l: 'Vulnerability Findings', p: '/client/findings', i: Bug, t: "What's exploitable and what needs remediation." },
  { l: 'Assets', p: '/client/assets', i: Server, t: 'The systems, cloud resources, and services in scope.' },
  { l: 'Sensitive Data', p: '/client/sensitive-data', i: FileSearch, t: 'Where high-value data lives and who can reach it.' },
  { l: 'OSINT', p: '/client/osint', i: Eye, t: 'External exposure, breached credentials, and public footprint.' },
  { section: 'Monitor' },
  { l: 'Attack Paths', p: '/client/attack-paths', i: GitBranch, t: 'Likely paths attackers could use to reach crown jewels.' },
  { l: 'Cloud Security', p: '/client/cloud-security', i: Cloud, t: 'AWS, Azure, M365, and Entra configuration risk.' },
  { l: 'AD Security', p: '/client/ad-security', i: Network, t: 'Identity, privilege, and domain compromise paths.' },
  { l: 'DAST', p: '/client/dast', i: Code2, t: 'Web app and API security testing results.' },
  { l: 'Compliance', p: '/client/compliance', i: ClipboardCheck, t: 'Control readiness and audit gaps.' },
  { l: 'Evidence', p: '/client/compliance/evidence', i: FileText, t: 'Compliance document repository.' },
  { l: 'Scans', p: '/client/scans', i: ScanLine, t: 'Scan history, artifacts, and job status.' },
  { l: 'Scan Schedule', p: '/client/scan-schedule', i: CalendarClock, t: 'Recurring scan timing and coverage.' },
  { section: 'Report' },
  { l: 'Reports', p: '/client/reports', i: FileText, t: 'Deliverables for clients, boards, auditors, and QBRs.' },
  { section: 'Config' },
  { l: 'Credentials', p: '/client/credentials', i: KeyRound, t: 'Connection credentials used to collect security data.' },
  { l: 'Secrets', p: '/client/secrets', i: Lock, t: 'Protected integration secrets and access material.' },
];

const MSP_NAV = [
  { l: 'MSP Dashboard', p: '/msp/dashboard', i: LayoutDashboard },
  { l: 'Clients', p: '/msp/clients', i: Briefcase },
  { l: 'Onboard Client', p: '/msp/onboard', i: UserPlus },
  { l: 'Team', p: '/msp/team', i: Users },
  { l: 'Alerts', p: '/msp/alerts', i: BellRing },
  { l: 'Reports', p: '/msp/reports', i: FileText },
  { l: 'Settings', p: '/msp/settings', i: Settings2 },
];

const PLATFORM_NAV = [
  { l: 'Platform Dashboard', p: '/platform/dashboard', i: LayoutDashboard },
  { l: 'White-Label Verify', p: '/platform/verify', i: ShieldCheck },
  { l: 'Platform Settings', p: '/platform/settings', i: Settings2 },
];

const SECTION_META = {
  Identify: {
    title: 'Know The Environment',
    sub: "What you have and what's wrong",
    tip: 'Inventory, exposure, sensitive data, and public footprint.',
  },
  Monitor: {
    title: 'Continuous Detection',
    sub: 'What changed or needs watching',
    tip: 'Attack paths, scan results, compliance movement, and live detection.',
  },
  Report: {
    title: 'Client Deliverables',
    sub: 'Board, audit, and QBR outputs',
    tip: 'Reports and packages for client conversations.',
  },
  Config: {
    title: 'Portal Setup',
    sub: 'Connections and access',
    tip: 'Credentials, integrations, and protected configuration.',
  },
};

function ScanIndicator() {
  const { isRunning, latest } = useActiveScan();

  if (isRunning) {
    return (
      <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.b1}`, background: C.s2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.grn, boxShadow: `0 0 6px ${C.grn}`, animation: 'pulse 1.2s ease-in-out infinite' }} />
          <span style={{ fontSize: 9, fontWeight: 700, color: C.grn, fontFamily: FONTS.mono, letterSpacing: 0, textTransform: 'uppercase' }}>
            {latest?.status === 'Queued' ? 'Scan Queued' : 'Scan Running'}
          </span>
        </div>
        <div style={{ height: 2, background: C.b1, borderRadius: 1, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: '60%', background: `linear-gradient(90deg,${C.grn},${C.sky})`, borderRadius: 1, animation: 'scanProgress 2s ease-in-out infinite' }} />
        </div>
        {latest?.profileName && (
          <div style={{ fontSize: 8, color: C.t3, fontFamily: FONTS.mono, marginTop: 4 }}>{latest.profileName}</div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.b1}` }}>
      <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>No active scan</div>
    </div>
  );
}

// Group nav items into sections for collapsible rendering
function groupNav(items) {
  const groups = [];
  let current = null;
  for (const item of items) {
    if (item.section) {
      current = { section: item.section, items: [] };
      groups.push(current);
    } else if (current) {
      current.items.push(item);
    } else {
      groups.push(item); // top-level items like Dashboard
    }
  }
  return groups;
}

function routeMatchesNavPath(path, navPath) {
  return path === navPath || path.startsWith(`${navPath}/`);
}

export function clientProfileMatchesSelection(profile, clientId, activeMspId = null) {
  const profileId = profile?.clientId ?? profile?.id;
  return Boolean(clientId && profileId &&
    String(profileId).toLowerCase() === String(clientId).toLowerCase() &&
    (!activeMspId || String(profile?.mspId ?? '').toLowerCase() === String(activeMspId).toLowerCase()));
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;
  const { isPlatformAdmin, isMspUser, isClientUser } = useUser();
  const branding = useBranding();
  // Determine which section the user is viewing, but restrict by role
  const isPlatform = path.startsWith('/platform') && isPlatformAdmin === true;
  const isMSP = path.startsWith('/msp') && (isMspUser === true || isPlatformAdmin === true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const brandPrimary = branding?.primaryColor || '#1a1a2e';
  const brandSecondary = branding?.secondaryColor || '#e94560';
  const brandLogoUrl = branding?.logoUrl || '';
  const brandPortalName = branding?.portalName || 'Sentinel Command';
  const brandLogoLetter = branding?.logoLetter || 'S';

  const { clientId, clientName: ctxName, setClientName } = useClient();
  const { impersonatedMsp } = useImpersonation();
  const activeMspId = impersonatedMsp?.mspId ?? null;
  const { data: loadedProfile } = useClientProfile();
  const profile = clientProfileMatchesSelection(loadedProfile, clientId, activeMspId) ? loadedProfile : null;
  const { data: rollup } = useMetricsRollup();
  const { data: sddPageFindings } = useSddFindings();
  const { summary: vulnSummary, total: vulnTotal } = useFindings({ pageSize: 500 });
  const { findings: rawFindings } = useAllFindings();
  const profileName = profile?.name || profile?.companyName || null;
  const clientName = profile ? (profileName || ctxName || 'Client') : 'Client';
  const clientTier = profile?.tier ?? 'Baseline';

  // Sidebar badges prefer the same visible finding records rendered by pages.
  const badgeCounts = useMemo(() => {
    if (!clientId || !profile) return {};
    const findings = (Array.isArray(rawFindings) ? rawFindings : []).map(normalizeFinding).filter(Boolean)
      .filter(f => f.status !== 'suppressed' && f.status !== 'possibly_remediated');
    const vuln = findings.filter(isVulnerabilityListFinding);
    const visibleVulnCount = vulnSummary?.total ?? vulnTotal ?? vuln.length;
    const sddSummary = summarizeVisibleSensitiveData(sddPageFindings);
    const osintRows = findings.filter(f => /osint|breach|hibp|dehashed|securitytrails/i.test(`${f.module} ${f.title} ${f.fingerprint}`));
    const osintDiagnostics = osintRows.filter(isOsintCoverageDiagnostic);
    const osint = osintRows.filter(f => !isOsintCoverageDiagnostic(f));
    const cloud = findings.filter(isCloudFinding);
    const ad = findings.filter(isADFinding);
    const dast = findings.filter(isDastFinding);
    const severeList = rows => rows.some(f => f.sev === 'critical' || f.sev === 'high');
    const modules = rollup?.modules ?? {};
    const severe = key => {
      const sev = modules[key]?.openSeverity ?? modules[key]?.severity ?? {};
      return (sev.critical ?? 0) + (sev.high ?? 0) > 0;
    };
    return {
      '/client/findings': { count: visibleVulnCount, urgent: severeList(vuln) || severe('findings') },
      '/client/assets': { count: 0, urgent: false },
      '/client/sensitive-data': { count: sddSummary.groupCount, urgent: severeList(sddSummary.findings) },
      '/client/osint': { count: Math.max(osint.length, rollup?.osint?.total ?? modules.osint?.total ?? 0), urgent: osintDiagnostics.length > 0 || (rollup?.osint?.coverageDiagnostics ?? 0) > 0 || severeList(osint) || severe('osint') },
      '/client/attack-paths': { count: rollup?.attackChains?.total ?? 0, urgent: (rollup?.attackChains?.active ?? 0) > 0 },
      '/client/cloud-security': { count: cloud.length || (modules.cloud?.total ?? 0), urgent: severeList(cloud) || severe('cloud') },
      '/client/ad-security': { count: ad.length || (modules.ad?.total ?? 0), urgent: severeList(ad) || severe('ad') },
      '/client/dast': { count: dast.length || (modules.dast?.total ?? 0), urgent: severeList(dast) || severe('dast') },
    };
  }, [clientId, profile, rawFindings, rollup, sddPageFindings, vulnSummary, vulnTotal]);

  // Select nav based on current route context, filtering platform-only items for non-admins
  const baseNav = isPlatform ? PLATFORM_NAV : isMSP ? MSP_NAV.filter(i => !i.platformOnly || isPlatformAdmin) : CLIENT_NAV;

  // Inject badges into nav items
  const navItems = baseNav.map(item =>
    item.p && badgeCounts[item.p]?.count > 0 ? { ...item, badge: badgeCounts[item.p].count, badgeUrgent: badgeCounts[item.p].urgent } : item
  );

  const groups = groupNav(navItems);

  // Sync profile name back to context if not yet set
  useEffect(() => {
    if (clientId && !ctxName && profileName && setClientName) {
      setClientName(profileName);
    }
  }, [clientId, ctxName, profileName, setClientName]);

  // Auto-expand sections that contain the active route
  const initCollapsed = () => {
    const c = {};
    for (const g of groups) {
      if (g.section) {
        const hasActive = g.items.some(i => routeMatchesNavPath(path, i.p));
        c[g.section] = !hasActive;
      }
    }
    return c;
  };
  const [collapsed, setCollapsed] = useState(initCollapsed);

  const toggle = section => setCollapsed(prev => ({ ...prev, [section]: !prev[section] }));

  // Auto-expand section containing active route on navigation
  useEffect(() => {
    for (const g of groups) {
      if (g.section && g.items.some(i => path === i.p || path.startsWith(i.p + '/'))) {
        setCollapsed(prev => prev[g.section] ? { ...prev, [g.section]: false } : prev);
      }
    }
  }, [path]);

  // Close mobile sidebar on navigation
  useEffect(() => { setMobileOpen(false); }, [path]);

  return (
    <>
      {/* Mobile hamburger button */}
      <button onClick={() => setMobileOpen(o => !o)} className="sidebar-hamburger" style={{
        display: 'none', position: 'fixed', top: 10, left: 10, zIndex: 1100,
        width: 36, height: 36, borderRadius: 8, border: `1px solid ${C.b1}`,
        background: C.s1, color: C.t1, fontSize: 18, cursor: 'pointer',
        alignItems: 'center', justifyContent: 'center',
      }}>{mobileOpen ? <X size={18} /> : <Menu size={18} />}</button>
      {/* Mobile overlay */}
      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} style={{ display: 'none', position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000 }} />}
      <style>{`
        @media (max-width: 768px) {
          .sidebar-hamburger { display: flex !important; }
          .sidebar-overlay { display: block !important; }
          .sidebar-aside { position: fixed !important; z-index: 1050; transform: translateX(${mobileOpen ? '0' : '-100%'}); transition: transform 0.2s ease; }
        }
      `}</style>
    <aside className="sidebar-aside" style={{ width: 226, background: C.s1, borderRight: `1px solid ${C.b1}`, display: 'flex', flexDirection: 'column', flexShrink: 0, height: '100vh', position: 'sticky', top: 0, boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.02)' }}>
      <div style={{ padding: '18px 16px 14px', borderBottom: `1px solid ${C.b1}`, display: 'flex', alignItems: 'center', gap: 9 }}>
        {brandLogoUrl ? (
          <BrandLogo
            src={brandLogoUrl}
            alt={brandPortalName}
            letter={brandLogoLetter}
            primaryColor={brandPrimary}
            secondaryColor={brandSecondary}
            width={178}
            height={54}
            maxWidth={178}
            maxHeight={54}
          />
        ) : (
          <>
            <div style={{ width: 34, height: 34, borderRadius: 6, background: `linear-gradient(135deg,${brandPrimary},${brandSecondary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: '#fff', flexShrink: 0 }}>{brandLogoLetter}</div>
            <span style={{ fontSize: 14, fontWeight: 800, color: C.t1, letterSpacing: 0, fontFamily: FONTS.sans, lineHeight: 1.15 }}>{brandPortalName}</span>
          </>
        )}
      </div>

      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.b1}` }}>
        <div style={{ background: `${C.s2}70`, border: `1px solid ${C.b1}`, borderRadius: 8, padding: '10px 11px' }}>
        {isPlatform ? (
          <>
            <div style={{ fontSize: 10, color: C.t2, textTransform: 'uppercase', letterSpacing: 0, fontFamily: FONTS.mono, marginBottom: 3 }}>Platform Admin</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, marginBottom: 5 }}>{brandPortalName}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {isMspUser && <button onClick={() => navigate('/msp/dashboard')} style={{ fontSize: 10, color: C.t2, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONTS.mono }}>MSP →</button>}
              <button onClick={() => navigate('/client/dashboard')} style={{ fontSize: 10, color: C.sky, background: `${C.sky}10`, border: `1px solid ${C.sky}33`, borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: FONTS.mono }}>Client →</button>
            </div>
          </>
        ) : isMSP ? (
          <>
            <div style={{ fontSize: 10, color: C.t2, textTransform: 'uppercase', letterSpacing: 0, fontFamily: FONTS.mono, marginBottom: 3 }}>MSP Partner</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, marginBottom: 5 }}>{brandPortalName}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {isPlatformAdmin && <button onClick={() => navigate('/platform/dashboard')} style={{ fontSize: 10, color: C.pur, background: `${C.pur}10`, border: `1px solid ${C.pur}33`, borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: FONTS.mono }}>← Platform</button>}
              <button onClick={() => navigate('/client/dashboard')} style={{ fontSize: 10, color: C.sky, background: `${C.sky}10`, border: `1px solid ${C.sky}33`, borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontFamily: FONTS.mono }}>Client →</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 10, color: C.t2, textTransform: 'uppercase', letterSpacing: 0, fontFamily: FONTS.mono, marginBottom: 3 }}>Client</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.t1, marginBottom: 5, lineHeight: 1.3 }}>{clientName}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <TierBadge value={clientTier} />
              {(isPlatformAdmin || isMspUser) && <button onClick={() => navigate(isPlatformAdmin ? '/platform/dashboard' : '/msp/dashboard')} style={{ fontSize: 10, color: C.t2, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONTS.mono }}>{isPlatformAdmin ? '← Platform' : '← MSP'}</button>}
            </div>
          </>
        )}
        </div>
      </div>

      <nav className="premium-scroll" style={{ flex: 1, padding: '7px 8px', overflowY: 'auto' }}>
        {groups.map((g, gIdx) => {
          // Top-level item (e.g. Dashboard) — no section wrapper
          if (!g.section) {
            const item = g;
            const act = path === item.p || path.startsWith(item.p + '/');
            return (
              <button key={item.p} onClick={() => navigate(item.p)} title={item.t || item.l} style={{
                display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                padding: '8px 9px', borderRadius: 7, border: `1px solid ${act ? C.b1 : 'transparent'}`, cursor: 'pointer',
                background: act ? C.s2 : 'transparent', color: act ? C.t1 : C.t2,
                fontSize: 12, fontWeight: act ? 600 : 400, textAlign: 'left',
                marginBottom: 1, transition: 'all 0.12s',
                borderLeft: act ? `2px solid ${brandSecondary}` : '2px solid transparent',
                boxShadow: act ? '0 8px 18px rgba(0,0,0,0.10)' : 'none',
                fontFamily: FONTS.sans,
              }}>
                <span style={{ display: 'inline-flex', opacity: 0.75, color: 'currentColor' }}>{item.i && <item.i size={14} strokeWidth={1.75} />}</span>
                <span style={{ flex: 1 }}>{item.l}</span>
                {item.badge > 0 && (
                  <span style={{ background: item.badgeUrgent ? C.redBg : C.s2, color: item.badgeUrgent ? C.red : C.t2, border: `1px solid ${item.badgeUrgent ? C.red + '44' : C.b1}`, borderRadius: 9999, fontSize: 9, fontWeight: 700, padding: '1px 5px', fontFamily: FONTS.mono }}>{item.badge}</span>
                )}
              </button>
            );
          }

          // Collapsible section
          const isCollapsed = collapsed[g.section];
          const sectionBadgeCount = g.items.reduce((sum, i) => sum + (i.badge || 0), 0);
          const sectionUrgent = g.items.some(i => i.badgeUrgent);
          const meta = SECTION_META[g.section] || { title: g.section, sub: '', tip: g.section };
          return (
            <div key={g.section}>
              <button onClick={() => toggle(g.section)} title={meta.tip} style={{
                display: 'flex', alignItems: 'center', width: '100%', gap: 4,
                fontSize: 9, color: C.t3, fontFamily: FONTS.sans, textTransform: 'none',
                fontWeight: 800,
                letterSpacing: 0, padding: '11px 9px 5px', marginTop: gIdx > 0 ? 2 : 0,
                background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
              }}>
                <span style={{
                  fontSize: 8, display: 'inline-block', transition: 'transform 0.15s',
                  transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                  color: C.t3,
                }}>▾</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', color: C.t2, fontSize: 10 }}>{meta.title}</span>
                  <span style={{ display: 'block', color: C.t3, fontSize: 9, fontWeight: 500, marginTop: 1 }}>{meta.sub}</span>
                </span>
                {isCollapsed && sectionBadgeCount > 0 && (
                  <span style={{ background: sectionUrgent ? C.redBg : C.s2, color: sectionUrgent ? C.red : C.t2, border: `1px solid ${sectionUrgent ? C.red + '44' : C.b1}`, borderRadius: 9999, fontSize: 8, fontWeight: 700, padding: '0px 4px', fontFamily: FONTS.mono }}>{sectionBadgeCount}</span>
                )}
              </button>
              <div style={{
                overflow: 'hidden', transition: 'max-height 0.2s ease, opacity 0.15s ease',
                maxHeight: isCollapsed ? 0 : 500, opacity: isCollapsed ? 0 : 1,
              }}>
                {g.items.map(item => {
                  const act = path === item.p || path.startsWith(item.p + '/');
                  return (
                    <button key={item.p} onClick={() => navigate(item.p)} title={item.t || item.l} style={{
                      display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                      padding: '8px 9px', borderRadius: 7, border: `1px solid ${act ? C.b1 : 'transparent'}`, cursor: 'pointer',
                      background: act ? C.s2 : 'transparent', color: act ? C.t1 : C.t2,
                      fontSize: 12, fontWeight: act ? 600 : 400, textAlign: 'left',
                      marginBottom: 1, transition: 'all 0.12s',
                      borderLeft: act ? `2px solid ${brandSecondary}` : '2px solid transparent',
                      boxShadow: act ? '0 8px 18px rgba(0,0,0,0.10)' : 'none',
                      fontFamily: FONTS.sans,
                    }}>
                      <span style={{ display: 'inline-flex', opacity: 0.75, color: 'currentColor' }}>{item.i && <item.i size={14} strokeWidth={1.75} />}</span>
                      <span style={{ flex: 1 }}>{item.l}</span>
                      {item.badge > 0 && (
                        <span style={{ background: item.badgeUrgent ? C.redBg : C.s2, color: item.badgeUrgent ? C.red : C.t2, border: `1px solid ${item.badgeUrgent ? C.red + '44' : C.b1}`, borderRadius: 9999, fontSize: 9, fontWeight: 700, padding: '1px 5px', fontFamily: FONTS.mono }}>{item.badge}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {!isMSP && !isPlatform && (profile ? <ScanIndicator /> : <div role="status" style={{ padding: '10px 14px' }}>Select a client to view scan status</div>)}
    </aside>
    </>
  );
}
